import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from 'react-router-dom';
import useCart from '../../Hook/useCart';
import api from '../../config/api';
import './Payment.css';
import logo from '../../assets/waseeny-logo.png';
import userAPI from '../../services/userAPI';
import { useTranslate } from '../../Hook/useTranslate';
import { getCurrencyFromPhone, PHONE_COUNTRIES, parseStoredPhone } from '../../utils/phone';
import { formatMoney } from '../../utils/formatMoney';
import { CheckoutSkeleton } from '../Common/PageSkeleton';

/* ── Icons ── */
function HelpIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <circle cx="10" cy="10" r="9" stroke="#555" strokeWidth="1.4" />
      <path d="M7.5 7.5a2.5 2.5 0 0 1 5 0c0 1.5-2.5 2-2.5 3.5" stroke="#555" strokeWidth="1.4" strokeLinecap="round" />
      <circle cx="10" cy="14.5" r="0.9" fill="#555" />
    </svg>
  );
}

function UserIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <circle cx="10" cy="7" r="3.5" stroke="#555" strokeWidth="1.4" />
      <path d="M3 18c0-3.866 3.134-7 7-7s7 3.134 7 7" stroke="#555" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function BackIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
      <path d="M14 5l-6 6 6 6" stroke="#222" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ChevronRight() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M7 5l4 4-4 4" stroke="#bbb" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function EuroIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
      <circle cx="11" cy="11" r="9" stroke="#444" strokeWidth="1.3" />
      <path d="M14 8c-1-1-3-1-4 0-1.5 1.5-1.5 4.5 0 6 1 1 3 1 4 0" stroke="#444" strokeWidth="1.3" strokeLinecap="round" />
      <path d="M7 10h5M7 12h5" stroke="#444" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}

function CardIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
      <rect x="2" y="6" width="18" height="12" rx="2" stroke="#444" strokeWidth="1.4" />
      <path d="M2 10h18" stroke="#444" strokeWidth="1.4" />
      <rect x="5" y="14" width="3" height="1.5" fill="#444" />
    </svg>
  );
}

function PlusBox() {
  return (
    <div style={{
      width: 36, height: 36,
      border: "1.5px solid #e62e2e",
      borderRadius: 8,
      display: "flex", alignItems: "center", justifyContent: "center",
      flexShrink: 0,
      background: "#fff",
    }}>
      <span style={{ color: "#e62e2e", fontSize: 20, lineHeight: 1, fontWeight: 400 }}>+</span>
    </div>
  );
}

/* ── Reusable Row Components ── */
function AddRow({ title, subtitle, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        display: "flex", alignItems: "center", gap: 14,
        background: "white", borderRadius: 10,
        padding: "14px 16px", cursor: "pointer",
        border: "1px solid #ece8e0",
        transition: "box-shadow 0.15s",
      }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)"}
      onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}
    >
      <PlusBox />
      <div>
        <div style={{ fontSize: 14, fontWeight: 600, color: "#e62e2e" }}>{title}</div>
        <div style={{ fontSize: 12, color: "#999", marginTop: 2 }}>{subtitle}</div>
      </div>
    </div>
  );
}

function OptionRow({ icon, title, subtitle, onClick, divider }) {
  return (
    <>
      <div
        onClick={onClick}
        style={{
          display: "flex", alignItems: "center", gap: 14,
          padding: "15px 16px", cursor: "pointer",
          transition: "background 0.12s",
        }}
        onMouseEnter={e => e.currentTarget.style.background = "#f9f6f2"}
        onMouseLeave={e => e.currentTarget.style.background = "transparent"}
      >
        <div style={{
          width: 40, height: 40,
          background: "#f3f0ea",
          borderRadius: 10,
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
        }}>
          {icon}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#222" }}>{title}</div>
          <div style={{ fontSize: 12, color: "#999", marginTop: 2 }}>{subtitle}</div>
        </div>
        <ChevronRight />
      </div>
      {divider && <div style={{ borderTop: "1px dashed #ddd", margin: "0 16px" }} />}
    </>
  );
}

/* ── Main Page ── */
export default function Payment() {
  const navigate = useNavigate();
  const location = useLocation();
  const { cart: backendCart, bill, loading: cartLoading } = useCart();
  const t = useTranslate();

  const [selected, setSelected] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [closedInfo, setClosedInfo] = useState(null);
  const [userData, setUserData] = useState(null);

  const restaurant = backendCart?.restaurant;
  const currencyInfo = React.useMemo(() => {
    const defaultCountry = PHONE_COUNTRIES.find(c => c.code === "IN");
    if (!restaurant?.contactNumber) {
      return { code: defaultCountry?.currency || "INR", symbol: defaultCountry?.symbol || defaultCountry?.currency || "" };
    }
    const parsed = parseStoredPhone(restaurant.contactNumber);
    const country = PHONE_COUNTRIES.find(c => c.code === parsed.countryCode);
    return {
      code: country?.currency || defaultCountry?.currency || "INR",
      symbol: country?.symbol || country?.currency || defaultCountry?.symbol || defaultCountry?.currency || ""
    };
  }, [restaurant]);

  const currencySymbol = currencyInfo.symbol;

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const data = await userAPI.getProfile();
        setUserData(data);
      } catch (err) {
        console.error("Error fetching user profile:", err);
      }
    };
    fetchUser();
  }, []);

  const handleHelp = () => {
    alert(t("Support is available 24/7. Please contact us at support@waseeny.com or call our hotline."));
  };

  // Helper to get item price
  const getItemPrice = (item) => {
    return item.price || item.unitPrice || 0;
  };

  // Currency Scaling Logic (Backend EUR -> Frontend INR)
  const calculatedSubtotal = backendCart?.items?.reduce((sum, i) => sum + (i.price * i.quantity), 0) || 0;
  const backendRawItemTotal = Number(bill?.itemTotal) || 0;
  const scalingFactor = (backendRawItemTotal > 0 && calculatedSubtotal > 0)
    ? (calculatedSubtotal / backendRawItemTotal)
    : 1;

  // Data extraction from backend cart
  const itemsCount = backendCart?.items?.reduce((sum, i) => sum + i.quantity, 0) || 0;
  const rawToPay = Number(bill?.toPay ?? location.state?.amount ?? 0) || 0;
  const totalAmount = rawToPay * scalingFactor;

  const addressId = location.state?.addressId || backendCart?.meta?.addressId;
  const deliveryAddress = location.state?.address || backendCart?.meta?.address || t("Address not specified");
  const deliveryTime = backendCart?.meta?.deliveryTime || 30;

  const getLangText = (data) => {
    if (!data) return "";
    if (typeof data === 'string') return data;
    return data.en || data.de || data.ar || Object.values(data)[0] || "";
  };

  const handlePayment = async (method) => {
    if (!method || processing) return;

    if (!addressId) {
      console.error(" Missing addressId");
      alert(t("Delivery address is missing. Please go back to checkout."));
      return;
    }

    try {
      setProcessing(true);
      setSelected(method);
      const backendMethod = method === 'online' ? 'online' : 'cod';

      // 1. Place Order
      const orderRes = await api.post('/orders/place', {
        addressId,
        paymentMethod: backendMethod,
        currency: currencyInfo.code,
        currencySymbol: currencyInfo.symbol
      });

      const createdOrder = orderRes.data?.order;
      if (!createdOrder || !createdOrder._id) {
        throw new Error("Order creation failed - No ID returned");
      }

      // 2. Stripe Redirect for online payment
      if (backendMethod === 'online') {
        const stripeRes = await api.post('/payment/create-checkout-session', {
          orderId: createdOrder._id,
          successUrl: `${window.location.origin}/order-success?order_id=${createdOrder._id}&session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${window.location.origin}/payment?error=cancelled`
        });

        if (stripeRes.data?.url) {
          window.location.assign(stripeRes.data.url);
          return;
        } else {
          console.error(" Stripe URL missing in response:", stripeRes.data);
          throw new Error("Stripe URL not returned from backend");
        }
      }

      const confirmedTotal = Number(createdOrder.totalAmount ?? orderRes.data?.totalPayment ?? totalAmount) || 0;

      // 3. Success for COD
      navigate('/order-success', {
        state: {
          order: {
            id: createdOrder._id,
            restaurant: getLangText(restaurant?.name),
            total: formatMoney(confirmedTotal, { symbol: currencySymbol, currency: currencyInfo.code })
          }
        }
      });
    } catch (error) {
      console.error(" Payment Error:", error);
      if (error.response?.status === 503 && error.response.data?.type === 'restaurant_unavailable') {
        setClosedInfo({
          message: error.response.data.error || "Restaurant Currently Closed",
          reason: error.response.data.reason
        });
      } else {
        alert(error.response?.data?.message || error.message || t("Order failed"));
      }
    } finally {
      setProcessing(false);
    }
  };

  if (cartLoading) return <CheckoutSkeleton />;

  return (
    <div className="payment-container">

      {/* Header */}
      <header className="payment-header">
        <div className="payment-header-left">
          <button onClick={() => navigate(-1)} className="back-btn">
            <BackIcon />
          </button>
          <img src={logo} alt="Waseeny" className="logo-image-small" />
          <div className="payment-title-group">
            <h1 className="payment-title">{t("safePayment")}</h1>
            <p className="payment-subtitle">{t("100% Secure Transaction")}</p>
          </div>
        </div>
        <div className="payment-header-right">
          <button
            onClick={handleHelp}
            className="help-btn-payment">
            <HelpIcon /> <span>{t("help")}</span>
          </button>
          <button
            onClick={() => navigate('/profile')}
            className="profile-btn-payment">
            <UserIcon /> <span>{userData?.name || t("account")}</span>
          </button>
        </div>
      </header>

      {/* Body */}
      <div className="payment-content">

        {/* Page title and items count */}
        <div style={{ marginBottom: "22px" }}>
          <h2 style={{ fontSize: "18px", fontWeight: "700", color: "#111", margin: "0" }}>{t("paymentMethods")}</h2>
          <p style={{ fontSize: "13px", color: "#666", marginTop: "4px" }}>
            {itemsCount} {t("items")} &nbsp; {t("total")} {formatMoney(totalAmount, { symbol: currencySymbol, currency: currencyInfo.code })}
          </p>
        </div>

        {/* Order Summary Card */}
        <div className="delivery-summary">
          <h3 className="delivery-restaurant">{restaurant ? getLangText(restaurant.name) : t("loading")}</h3>
          <div className="delivery-address">
            <span className="delivery-label">{t("deliveryAddress")}:</span> {deliveryAddress}
          </div>
          <div style={{ marginTop: "8px", fontSize: "13px", color: "#666" }}>
            <span className="delivery-label">{t("deliveryIn")}:</span> <strong>{deliveryTime} {t("min")}</strong>
          </div>
        </div>

        {/* Payment Methods Section */}
        <div className="payment-methods">

          {/* Online Payment (Card, KLARNA, etc) */}
          <div className="payment-section-group">
            <div className="section-group-title">{t("onlinePaymentStripe")}</div>
            <div
              className={`payment-method-card ${selected === 'online' ? 'selected' : ''}`}
              onClick={() => setSelected("online")}
              style={{ cursor: "pointer", border: selected === 'online' ? "2px solid #e62e2e" : "none" }}
            >
              <div className="payment-method-icon">
                <CardIcon />
              </div>
              <div className="payment-method-info">
                <div className="payment-method-title">{t("Debit/Credit Card / KLARNA")}</div>
                <div style={{ fontSize: "12px", color: "#666" }}>{t("Secure payment via Stripe")}</div>
              </div>
              <div style={{ marginLeft: "auto" }}>
                {selected === 'online' && <div style={{ color: "#e62e2e", fontWeight: "700" }}>✓</div>}
              </div>
            </div>
          </div>

          {/* Cash on Delivery */}
          <div className="payment-section-group">
            <div className="section-group-title">{t("barzahlung")}</div>
            <div
              className={`payment-method-card ${selected === 'cod' ? 'selected' : ''}`}
              onClick={() => setSelected("cod")}
              style={{ cursor: "pointer", border: selected === 'cod' ? "2px solid #e62e2e" : "none" }}
            >
              <div className="payment-method-icon">
                <EuroIcon />
              </div>
              <div className="payment-method-info">
                <div className="payment-method-title">{t("payOnDelivery")}</div>
                <div style={{ fontSize: "12px", color: "#666" }}>{t("doorstepPayment")}</div>
              </div>
              <div style={{ marginLeft: "auto" }}>
                {selected === 'cod' && <div style={{ color: "#e62e2e", fontWeight: "700" }}>✓</div>}
              </div>
            </div>
          </div>

        </div>

        {/* Action Button */}
        <div style={{ marginTop: "32px" }}>
          <button
            onClick={() => handlePayment(selected)}
            className="pay-now-btn"
            style={{
              width: "100%",
              padding: "16px",
              background: selected ? "#e62e2e" : "#ccc",
              color: "white",
              border: "none",
              borderRadius: "12px",
              fontSize: "17px",
              fontWeight: "700",
              cursor: selected ? "pointer" : "not-allowed",
              boxShadow: selected ? "0 4px 12px rgba(230, 46, 46, 0.2)" : "none"
            }}
            disabled={!selected || processing}
          >
            {processing ? t("processingTitle") : selected ? `${t("payNow")} (${formatMoney(totalAmount, { symbol: currencySymbol, currency: currencyInfo.code })})` : "Select Payment Method"}
          </button>
        </div>

        {/* Restaurant Closed Modal */}
        {closedInfo && (
          <div className="confirmation-modal-overlay">
            <div className="confirmation-modal">
              <div style={{ fontSize: 60, marginBottom: 15 }}>🏪</div>
              <h2 className="confirmation-title">{closedInfo.message}</h2>
              <p className="confirmation-message">
                {closedInfo.reason || t("restaurantClosed")}
              </p>
              <button
                onClick={() => setClosedInfo(null)}
                className="view-booking-btn"
              >
                {t("okayOrderLater")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}